import type { SessionEvent, ContextUpdateData } from '../types/sessions.js';
import type {
  ProcessedState,
  ChatMessage,
  ChatState,
  ProgressTree,
  ProgressNode,
  ProgressStep,
  LlmCallPurpose,
  LoopExitReason,
  ClarificationQuestion,
  PlatformApprovalRequest,
  WebActionApprovalRequest,
  MediaActionApprovalRequest,
} from './types.js';
import { normalizeInternalAgentProvenance } from './internal-agent-provenance.js';

interface InternalRun {
  runIndex: number;
  startedAt: string;
  endedAt: string | null;
  status: 'in_progress' | 'completed' | 'failed' | 'stopped';
  error?: string;
  workspaceRefusal?: string;
  steps: ProgressStep[];
  nodes: ProgressNode[];
  contextUsagePercent: number;
  workflowId?: string;
  assistantMessageId?: string;
  agentConfig?: Record<string, unknown>;
  toolNames?: string[];
  selectedProcess?: { id: string; name: string; reason: string };
  internalAgents: NonNullable<ProgressTree['internalAgents']>;
}

function captureInternalAgent(run: InternalRun, e: SessionEvent): void {
  const provenance = normalizeInternalAgentProvenance({
    personaName: e.data.internal_agent_persona_name as string | undefined,
    processId: e.data.internal_agent_process_id as string | undefined,
    processName: e.data.internal_agent_process_name as string | undefined,
  });
  if (!provenance) return;
  const key = `${provenance.personaName}:${provenance.processId ?? ''}`;
  if (!run.internalAgents.some((item) => `${item.personaName}:${item.processId ?? ''}` === key)) {
    run.internalAgents.push(provenance);
  }
}

function findNode(run: InternalRun, id: string): ProgressNode | undefined {
  return run.nodes.find(n => n.id === id);
}

function handleTaskStarted(run: InternalRun, e: SessionEvent): void {
  const id = e.data.id as string;
  if (run.nodes.some(n => n.id === id)) return;
  const personaName = (e.data.persona_name as string) ?? '';
  const stepId = typeof e.data.step_id === 'string' ? e.data.step_id : undefined;
  const processId = e.data.process_id as string | undefined;
  const processName = e.data.process_name as string | undefined;
  run.nodes.push({
    id,
    parentId: e.data.parent_id as string | undefined,
    label: (e.data.label ?? personaName) as string,
    status: 'running',
    steps: [],
    children: [],
    startedAt: e.created_at,
    cognitiveToolType: e.data.cognitive_tool_type as string | undefined,
    stepId,
    personaName,
    processId,
    processName,
    internalAgent: normalizeInternalAgentProvenance({ personaName, processId, processName }),
    fanoutId: typeof e.data.fanout_id === 'string' ? e.data.fanout_id : undefined,
    fanoutSize: typeof e.data.fanout_size === 'number' ? e.data.fanout_size : undefined,
    promptPreview: e.data.prompt_preview as string | undefined,
    artifacts: [],
    decisions: [],
    tokenCount: 0,
  });
}

function handleTaskCompleted(run: InternalRun, e: SessionEvent): void {
  const id = e.data.id as string;
  const node = findNode(run, id);
  if (node) {
    node.status = (e.data.status as 'completed' | 'failed') ?? 'completed';
    node.conclusionSummary = e.data.conclusion_summary as string | undefined;
    node.durationMs = e.data.duration_ms as number | undefined;
    node.error = e.data.error as string | undefined;
    node.endedAt = e.created_at;
    node.artifacts = (e.data.artifacts as string[]) ?? node.artifacts;
    node.decisions = (e.data.decisions as string[]) ?? node.decisions;
    node.tokenCount = (e.data.token_count as number) ?? node.tokenCount;
    if (e.data.cognitive_tool_type) {
      node.cognitiveToolType = e.data.cognitive_tool_type as string;
    }
    if (typeof e.data.exit_reason === 'string') {
      node.exitReason = e.data.exit_reason as LoopExitReason;
    }
    if (Array.isArray(e.data.failed_tools)) {
      node.failedTools = e.data.failed_tools as string[];
    }
  }
}

function findStepByCallId(run: InternalRun, callId: string): ProgressStep | undefined {
  const inRoot = run.steps.find((s) => s.callId === callId);
  if (inRoot) return inRoot;
  for (const node of run.nodes) {
    const found = node.steps.find((s) => s.callId === callId);
    if (found) return found;
  }
  return undefined;
}

function llmCompletionFields(e: SessionEvent): Partial<ProgressStep> {
  return {
    hasToolCalls: (e.data.has_tool_calls as boolean) ?? false,
    durationMs: e.data.duration_ms as number | undefined,
    tokens: e.data.tokens as number | undefined,
    promptTokens: e.data.prompt_tokens as number | undefined,
    completionTokens: e.data.completion_tokens as number | undefined,
    tokensPerSecond: e.data.tokens_per_second as number | undefined,
    modelName: e.data.model_name as string | undefined,
    providerType: e.data.provider_type as string | undefined,
    thinkingPreview: e.data.thinking_preview as string | undefined,
    responsePreview: e.data.response_preview as string | undefined,
    toolNames: e.data.tool_names as string[] | undefined,
    error: e.data.error as string | undefined,
  };
}

function completeLlmStep(step: ProgressStep, fields: Partial<ProgressStep>): void {
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) (step as unknown as Record<string, unknown>)[key] = value;
  }
  step.status = fields.error ? 'failed' : 'completed';
}

function closeLlmStepByCallId(run: InternalRun, e: SessionEvent, fields: Partial<ProgressStep>): boolean {
  const callId = typeof e.data.call_id === 'string' ? e.data.call_id : undefined;
  if (!callId) return false;
  const step = findStepByCallId(run, callId);
  if (!step) return false;
  completeLlmStep(step, fields);
  if (!step.durationMs && step.startedAt) {
    const elapsed = new Date(e.created_at).getTime() - new Date(step.startedAt).getTime();
    if (elapsed > 0) step.durationMs = elapsed;
  }
  return true;
}

function handleTaskFailed(run: InternalRun, e: SessionEvent): void {
  const id = e.data.id as string;
  const node = findNode(run, id);
  if (node) {
    node.status = 'failed';
    node.error = (e.data.error as string) ?? 'Agent failed';
    node.endedAt = e.created_at;
  }
}

function appendStepToRun(run: InternalRun, e: SessionEvent): void {
  const nodeId = e.data.id as string | undefined;

  const targetSteps = nodeId ? findNode(run, nodeId)?.steps : undefined;
  const steps = targetSteps ?? run.steps;

  switch (e.event_type) {
    case 'triage':
      steps.push({
        type: 'triage',
        timestamp: e.created_at,
        nodeId,
        iteration: 0,
        guidancePreview: e.data.guidance_preview as string | undefined,
      });
      break;

    case 'llm_call_start': {
      const callId = typeof e.data.call_id === 'string' ? e.data.call_id : undefined;
      const existing = callId ? findStepByCallId(run, callId) : undefined;
      if (existing) {
        existing.startedAt = e.created_at;
        break;
      }
      steps.push({
        type: 'llm_call',
        timestamp: e.created_at,
        startedAt: e.created_at,
        status: 'running',
        callId,
        purpose: (e.data.purpose as LlmCallPurpose | undefined) ?? 'loop',
        nodeId,
        iteration: (e.data.iteration as number) ?? 0,
        modelName: e.data.model_name as string | undefined,
        providerType: e.data.provider_type as string | undefined,
        promptTokensEstimate: e.data.prompt_tokens_estimate as number | undefined,
        deferred: e.data.deferred === true,
        internalAgent: normalizeInternalAgentProvenance({
          personaName: e.data.internal_agent_persona_name as string | undefined,
          processId: e.data.internal_agent_process_id as string | undefined,
          processName: e.data.internal_agent_process_name as string | undefined,
        }),
      });
      break;
    }

    case 'llm_call': {
      const fields = llmCompletionFields(e);
      if (closeLlmStepByCallId(run, e, fields)) break;
      const step: ProgressStep = {
        type: 'llm_call',
        timestamp: e.created_at,
        callId: typeof e.data.call_id === 'string' ? e.data.call_id : undefined,
        purpose: (e.data.purpose as LlmCallPurpose | undefined) ?? 'loop',
        nodeId,
        iteration: (e.data.iteration as number) ?? 0,
      };
      completeLlmStep(step, fields);
      steps.push(step);
      break;
    }

    case 'process_evaluation':
    case 'process_derivation':
      closeLlmStepByCallId(run, e, {
        modelName: e.data.model_name as string | undefined,
        providerType: e.data.provider_type as string | undefined,
      });
      break;

    case 'tool_call_start':
      steps.push({
        type: 'tool_call',
        timestamp: e.created_at,
        toolName: e.data.tool_name as string | undefined,
        toolArgsPreview: e.data.args_preview as string | undefined,
        nodeId,
        childIdAtStart: e.data.child_id as string | undefined,
        iteration: (e.data.iteration as number) ?? 0,
      });
      break;

    case 'tool_call_result': {
      const toolName = e.data.tool_name as string | undefined;
      for (let j = steps.length - 1; j >= 0; j--) {
        if (steps[j].type === 'tool_call' && steps[j].toolName === toolName && !steps[j].toolResultStatus) {
          steps[j].toolResultStatus = e.data.status as string;
          steps[j].toolResultError = e.data.error as string | undefined;
          steps[j].toolResultPreview =
            (e.data.result_preview as string | undefined) || (e.data.error as string | undefined);
          if (e.data.child_id) {
            steps[j].childId = e.data.child_id as string;
          }
          const startTime = new Date(steps[j].timestamp).getTime();
          const endTime = new Date(e.created_at).getTime();
          if (startTime > 0 && endTime > startTime) {
            steps[j].durationMs = endTime - startTime;
          }
          break;
        }
      }
      break;
    }

    case 'context_update': {
      // Typed payload. The
      // headline fields are OPTIONAL — omitted when the provider returned no
      // usage — so usagePercent stays undefined (the step renders no percent,
      // never a fabricated 0%). supersededEntries feeds the dedupe subtitle.
      const d = e.data as unknown as ContextUpdateData;
      steps.push({
        type: 'context_update',
        timestamp: e.created_at,
        nodeId,
        usagePercent: d.usage_percent,
        overflowHandled: d.overflow_handled ?? false,
        iteration: d.iteration ?? 0,
        totalContextTokens: d.total_tokens,
        contextWindowSize: d.context_window_size,
        supersededEntries: d.superseded_entries ?? [],
        promptTokens: d.prompt_tokens,
        completionTokens: d.completion_tokens,
        attribution: d.attribution,
        inContextEntries: d.in_context_entries,
        compactedEntries: d.compacted_entries,
        messageCount: d.message_count,
        personaName: d.persona_name,
        staleMessageCount: d.stale_message_count,
        tokensSavedByDedup: d.tokens_saved_by_dedup,
        messagesSummarized: d.messages_summarized,
        tokensReduced: d.tokens_reduced,
        summarizerProviderType: d.summarizer_provider_type,
        summarizerModelName: d.summarizer_model_name,
      });
      break;
    }
  }
}

function buildNodeTree(nodes: ProgressNode[]): void {
  const byId = new Map<string, ProgressNode>();
  for (const node of nodes) {
    byId.set(node.id, node);
    node.children = [];
  }

  for (const node of nodes) {
    if (node.parentId) {
      const parent = byId.get(node.parentId);
      if (parent) {
        parent.children.push(node);
      }
    }
  }
}

function rerouteOrphanedSteps(run: InternalRun): void {
  if (run.steps.length === 0) return;
  const nodeMap = new Map(run.nodes.map(n => [n.id, n]));
  if (nodeMap.size === 0) return;

  const movedToNodes = new Set<string>();
  for (let i = run.steps.length - 1; i >= 0; i--) {
    const step = run.steps[i];
    if (step.nodeId && nodeMap.has(step.nodeId)) {
      const node = nodeMap.get(step.nodeId)!;
      node.steps.push(step);
      run.steps.splice(i, 1);
      movedToNodes.add(step.nodeId);
    }
  }

  for (const id of movedToNodes) {
    const node = nodeMap.get(id)!;
    node.steps.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  }
}

function runToProgressTree(run: InternalRun): ProgressTree {
  return {
    runIndex: run.runIndex,
    status: run.status,
    startedAt: run.startedAt,
    endedAt: run.endedAt,
    error: run.error,
    workspaceRefusal: run.workspaceRefusal,
    steps: run.steps,
    nodes: run.nodes,
    contextUsagePercent: run.contextUsagePercent,
    assistantMessageId: run.assistantMessageId,
    agentConfig: run.agentConfig,
    toolNames: run.toolNames,
    selectedProcess: run.selectedProcess,
    internalAgents: run.internalAgents,
  };
}

type PlatformApprovalSignal =
  | { kind: 'pending'; request: PlatformApprovalRequest }
  | { kind: 'cleared' }
  | { kind: 'none' };

function readPlatformApproval(e: SessionEvent): PlatformApprovalSignal {
  const preview = e.data.result_preview;
  if (typeof preview !== 'string') return { kind: 'none' };

  let parsed: unknown;
  try {
    parsed = JSON.parse(preview);
  } catch {
    return { kind: 'none' };
  }
  if (!parsed || typeof parsed !== 'object') return { kind: 'none' };

  const obj = parsed as Record<string, unknown>;
  if (obj.status === 'confirmation_required') {
    const key = obj.idempotency_key;
    const p = obj.preview;
    if (typeof key === 'string' && p && typeof p === 'object') {
      const pv = p as Record<string, unknown>;
      if (typeof pv.resource === 'string' && typeof pv.method === 'string') {
        return {
          kind: 'pending',
          request: { resource: pv.resource, method: pv.method, idempotencyKey: key },
        };
      }
    }
    return { kind: 'none' };
  }

  if (e.data.tool_name === 'platform_call') {
    return { kind: 'cleared' };
  }
  return { kind: 'none' };
}

/**
 * Resolve the run an event belongs to. Prefers the event's own `run_id`; falls
 * back to the open run only for histories recorded before the key existed.
 */
function runForEvent(
  runsById: Map<string, InternalRun>,
  currentRun: InternalRun | null,
  e: SessionEvent,
): InternalRun | null {
  const key = typeof e.data.run_id === 'string' ? e.data.run_id : '';
  if (key) {
    const keyed = runsById.get(key);
    if (keyed) return keyed;
  }
  return currentRun;
}

export function processEvents(rawEvents: SessionEvent[]): ProcessedState {
  const seen = new Set<number>();
  const events: SessionEvent[] = [];
  for (const e of rawEvents) {
    if (!seen.has(e.sequence_num)) {
      seen.add(e.sequence_num);
      events.push(e);
    }
  }
  events.sort((a, b) => a.sequence_num - b.sequence_num);

  const messages: ChatMessage[] = [];
  const runs: InternalRun[] = [];
  // Runs are addressed by `run_id`, not by a moving pointer. With two concurrent
  // roots the pointer model could never close the first one — every terminal
  // handler mutated whatever run happened to be current — so it rendered
  // in_progress forever, and re-folding a persisted history reproduced it
  // exactly (agents ADR-043).
  const runsById = new Map<string, InternalRun>();
  let currentRun: InternalRun | null = null;
  let pendingClarification: ClarificationQuestion | null = null;
  let pendingPlatformApproval: PlatformApprovalRequest | null = null;
  let pendingWebActionApproval: WebActionApprovalRequest | null = null;
  let pendingMediaActionApproval: MediaActionApprovalRequest | null = null;
  let workflowId: string | null = null;
  let lastStopReason: string | null = null;

  for (const e of events) {
    if (currentRun) captureInternalAgent(currentRun, e);
    switch (e.event_type) {
      case 'reasoning_started':
        currentRun = {
          runIndex: runs.length,
          startedAt: e.created_at,
          endedAt: null,
          status: 'in_progress',
          steps: [],
          nodes: [],
          internalAgents: [],
          contextUsagePercent: 0,
        };
        if (e.data.workflow_id) {
          currentRun.workflowId = e.data.workflow_id as string;
          workflowId = e.data.workflow_id as string;
        }
        if (typeof e.data.selected_process_id === 'string' && typeof e.data.selected_process_name === 'string' && typeof e.data.selected_process_reason === 'string') {
          currentRun.selectedProcess = {
            id: e.data.selected_process_id,
            name: e.data.selected_process_name,
            reason: e.data.selected_process_reason,
          };
        }
        captureInternalAgent(currentRun, e);
        runs.push(currentRun);
        if (typeof e.data.run_id === 'string' && e.data.run_id) {
          runsById.set(e.data.run_id, currentRun);
        }
        pendingClarification = null;
        pendingWebActionApproval = null;
        pendingMediaActionApproval = null;
        lastStopReason = null;
        break;

      case 'state_initialized':
        if (currentRun) {
          currentRun.agentConfig = e.data.agent_config as Record<string, unknown> | undefined;
          currentRun.toolNames = e.data.tool_names as string[] | undefined;
        }
        break;

      case 'reasoning_completed': {
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.status = 'completed';
          target.endedAt = e.created_at;
          if (target === currentRun) currentRun = null;
        }
        lastStopReason = null;
        break;
      }

      case 'reasoning_failed': {
        // (agents ADR-96511)
        const refusal =
          typeof e.data.workspace_refusal === 'string' ? e.data.workspace_refusal : undefined;
        const refusalMessage =
          typeof e.data.workspace_refusal_message === 'string'
            ? e.data.workspace_refusal_message
            : undefined;
        const err = refusalMessage ?? (e.data.error as string) ?? 'Reasoning failed';
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.status = 'failed';
          target.endedAt = e.created_at;
          target.error = err;
          target.workspaceRefusal = refusal;
          if (target === currentRun) currentRun = null;
        } else {
          // No run to attribute to — a pre-run gate failed before it started.
          // Synthesize one so the failure is visible, and register it under its
          // run_id so any later keyed event still finds it (agents ADR-043).
          const failed: InternalRun = {
            runIndex: runs.length,
            startedAt: e.created_at,
            endedAt: e.created_at,
            status: 'failed',
            error: err,
            workspaceRefusal: refusal,
            steps: [],
            nodes: [],
            internalAgents: [],
            contextUsagePercent: 0,
          };
          runs.push(failed);
          if (typeof e.data.run_id === 'string' && e.data.run_id) {
            runsById.set(e.data.run_id, failed);
          }
          currentRun = null;
        }
        lastStopReason = null;
        break;
      }

      case 'execution_stopped': {
        const target = runForEvent(runsById, currentRun, e);
        if (target) {
          target.status = 'stopped';
          target.endedAt = e.created_at;
          // A stopped run's nodes are stopped too. Leaving them 'running' left
          // permanent spinners inside a run the user had already ended.
          for (const node of target.nodes) {
            if (node.status === 'running') {
              node.status = 'stopped';
              node.endedAt = e.created_at;
            }
          }
          if (target === currentRun) currentRun = null;
        }
        lastStopReason = 'user_stop';
        break;
      }

      case 'execution_redirected':
        if (currentRun) {
          currentRun.status = 'completed';
          currentRun.endedAt = e.created_at;
          currentRun = null;
        }
        lastStopReason = 'user_redirect';
        break;

      case 'user_message':
        messages.push({
          id: e.id,
          role: 'user',
          content: (e.data.content as string) ?? '',
          timestamp: e.created_at,
          metadata: e.data.metadata as Record<string, unknown> | undefined,
        });
        break;

      case 'assistant_message': {
        messages.push({
          id: e.id,
          role: 'assistant',
          content: (e.data.content as string) ?? '',
          timestamp: e.created_at,
          metadata: e.data.metadata as Record<string, unknown> | undefined,
        });
        // Final replies usually emit before reasoning_completed, while the run
        // is still in_progress. Prefer the active run; only fall back to the
        // latest completed run without an id (late/out-of-order frames).
        const messageId = (e.data.message_id as string) ?? e.id;
        if (currentRun && !currentRun.assistantMessageId) {
          currentRun.assistantMessageId = messageId;
        } else {
          const closedRun = [...runs].reverse().find(
            (r) => r.status !== 'in_progress' && !r.assistantMessageId,
          );
          if (closedRun) {
            closedRun.assistantMessageId = messageId;
          }
        }
        break;
      }

      case 'clarification_request':
        pendingClarification = {
          questionId: (e.data.question_id as string) ?? '',
          question: (e.data.question as string) ?? '',
          options: (e.data.options as Array<{ label: string; description: string }>) ?? [],
        };
        break;

      case 'clarification_response':
        pendingClarification = null;
        break;

      case 'web_action_approval_request': {
        const approvalId = e.data.approval_id;
        const toolName = e.data.tool_name;
        const args = e.data.arguments;
        if (typeof approvalId === 'string' && typeof toolName === 'string' && args && typeof args === 'object') {
          const workflowId = e.data.workflow_id;
          pendingWebActionApproval = {
            approvalId,
            toolName,
            arguments: args as Record<string, unknown>,
            ...(typeof workflowId === 'string' && workflowId.length > 0
              ? { workflowId }
              : {}),
          };
        }
        break;
      }

      case 'web_action_approval_response':
        pendingWebActionApproval = null;
        break;

      case 'media_action_approval_request': {
        const approvalId = e.data.approval_id;
        const toolName = e.data.tool_name;
        const args = e.data.arguments;
        if (typeof approvalId === 'string' && typeof toolName === 'string' && args && typeof args === 'object') {
          const workflowId = e.data.workflow_id;
          pendingMediaActionApproval = {
            approvalId,
            toolName,
            arguments: args as Record<string, unknown>,
            ...(typeof workflowId === 'string' && workflowId.length > 0
              ? { workflowId }
              : {}),
          };
        }
        break;
      }

      case 'media_action_approval_response':
        pendingMediaActionApproval = null;
        break;

      case 'task_started':
      case 'step_started':
        if (currentRun) handleTaskStarted(currentRun, e);
        break;

      case 'task_completed':
      case 'step_completed':
        if (currentRun) handleTaskCompleted(currentRun, e);
        break;

      case 'task_failed':
      case 'step_failed':
        if (currentRun) handleTaskFailed(currentRun, e);
        break;

      case 'tool_call_result': {
        if (currentRun) appendStepToRun(currentRun, e);
        const signal = readPlatformApproval(e);
        if (signal.kind === 'pending') {
          pendingPlatformApproval = signal.request;
        } else if (signal.kind === 'cleared') {
          pendingPlatformApproval = null;
        }
        break;
      }

      default:
        if (currentRun) appendStepToRun(currentRun, e);
        break;
    }
  }

  for (const run of runs) {
    buildNodeTree(run.nodes);
    rerouteOrphanedSteps(run);
  }

  // The session is reasoning while ANY run is open, not merely the last one.
  // Reading only the last run reported 'idle' with a workflow still executing,
  // which is what let a second send slip past the queue gate (agents ADR-043).
  const lastRun = runs[runs.length - 1] as InternalRun | undefined;
  const anyOpen = runs.some((r) => r.status === 'in_progress');
  let state: ChatState = 'idle';
  if (anyOpen) {
    state = 'reasoning';
  } else if (lastRun) {
    if (lastRun.status === 'failed') {
      state = 'error';
    } else if (lastRun.status === 'stopped' || lastStopReason === 'user_stop') {
      state = 'stopped';
    }
  }

  const reasoningRuns = runs.map(runToProgressTree);

  const progressTree = lastRun ? runToProgressTree(lastRun) : null;

  return {
    messages,
    state,
    progressTree,
    pendingClarification,
    pendingPlatformApproval,
    pendingWebActionApproval,
    pendingMediaActionApproval,
    workflowId,
    reasoningRuns,
    lastStopReason,
  };
}
